import { FocusMonitor } from '@angular/cdk/a11y'
import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Optional,
  Output,
  Self,
} from '@angular/core'
import {
  FormGroupDirective,
  NgControl,
  NgForm,
  UntypedFormControl,
  UntypedFormGroup,
} from '@angular/forms'
import { MatFormFieldControl } from '@angular/material/form-field'
import {
  AddressSchema,
  ClinicService,
  Countries,
  GoogleMapsApiLoaderService,
  IntakeService,
  SelectOption,
  standardizePhone,
  States,
  StateSelectOption,
} from '@mmx/shared'
import { isEqual, isNil } from 'lodash'
import { Observable, ReplaySubject, Subject } from 'rxjs'
import { first, map, startWith, takeUntil, tap } from 'rxjs/operators'

import { CustomMaterialInputControlComponent } from '../custom-field'

export type PlaceType =
  | 'premise'
  | 'health'
  | 'point_of_interest'
  | 'establishment'

export interface GooglePlace {
  address_components: {
    long_name: string
    short_name: string
    types: string[]
  }[]
  /**
   * HTML formatted address
   * <span class=\"street-address\">123 Longwood Ave</span>,
   * <span class=\"locality\">Brookline</span>,
   * <span class=\"region\">MA</span> <span class=\"postal-code\">02446-6625</span>,
   * <span class=\"country-name\">USA</span>
   */
  adr_address: string
  business_status?: 'OPERATIONAL' | string
  /**
   * Address formatted in single line
   * 123 Longwood Ave, Brookline, MA 02446, USA
   */
  formatted_address: string
  international_phone_number?: string
  formatted_phone_number?: string
  geometry: {
    location: {
      lat: () => string
      lng: () => string
    }
    viewport: any
  }
  html_attributions: any[]
  icon: string
  icon_background_color: string
  icon_mask_base_uri: string
  /**
   * "123 Longwood Ave"
   */
  name: string
  opening_hours?: { periods: any[]; weekday_text: any[]; isOpen: () => boolean }
  photos: any[]
  place_id: string // "ChIJ2UkIgZN544kRJkbUUhRSB7Y"
  plus_code?: {
    compound_code: string
    global_code: string
  }
  rating?: number
  reference: string // "ChIJ2UkIgZN544kRJkbUUhRSB7Y"
  reviews?: any[]
  user_ratings_total?: number
  types: PlaceType[]
  /**
   * Direct link to business listing
   * "https://maps.google.com/?q=123+Longwood+Ave,+Brookline,+MA+02446,+USA&ftid=0x89e37993810849d9:0xb607521452d44626"
   */
  url: string
  utc_offset: number
  utc_offset_minutes: number
  vicinity: string
  website?: string
}

@Component({
  selector: 'mmx-address-field',
  templateUrl: './address-field.component.html',
  providers: [
    {
      provide: MatFormFieldControl,
      useExisting: AddressFormFieldControlComponent,
    },
  ],
  standalone: false,
})
export class AddressFormFieldControlComponent
  extends CustomMaterialInputControlComponent<AddressSchema>
  implements OnInit, OnDestroy, AfterViewInit
{
  @Input() autocomplete: string
  @Input() nameControl: UntypedFormControl
  @Input() phoneControl: UntypedFormControl
  @Input() placeSelector: boolean | HTMLInputElement
  @Input() placeTypes: PlaceType[]
  @Input() form: UntypedFormGroup
  @Input() countyFieldEnabled = false
  @Output() placeSelected = new EventEmitter<any>()

  countries = Countries
  states?: StateSelectOption[]

  filteredCountries: ReplaySubject<SelectOption<string>[]> = new ReplaySubject<SelectOption<string>[]>(1)
  countryFilterCtrl: UntypedFormControl = new UntypedFormControl()

  filteredStates: ReplaySubject<SelectOption<string>[]> = new ReplaySubject<SelectOption<string>[]>(1)
  stateFilterCtrl: UntypedFormControl = new UntypedFormControl()
  countyFieldVisible$: Observable<Boolean>

  protected _placeholder = 'Address'

  private _onDestroy = new Subject<void>()
  private gaAutocomplete: any

  private get defaultCountry(): string {
    try {
      return this.clinicService.clinic.settings.defaultCountry ?? 'US'
    } catch (ex) {
      return 'US'
    }
  }

  constructor(
    @Optional() @Self() ngControl: NgControl,
    @Optional() parentForm: NgForm,
    @Optional() parentFormGroup: FormGroupDirective,
    protected fm: FocusMonitor,
    protected elRef: ElementRef,
    protected clinicService: ClinicService,
    protected intakeService: IntakeService,
    private googleMapsApiLoaderService: GoogleMapsApiLoaderService,
  ) {
    super(ngControl, parentForm, parentFormGroup, fm, elRef)
  }

  ngOnInit() {
    if (isNil(this._value)) {
      this._value = {}
    }

    super.ngOnInit()
    if (!this.form) {
      this.form = new UntypedFormGroup({
        name: new UntypedFormControl(),
        address1: new UntypedFormControl(),
        address2: new UntypedFormControl(),
        address3: new UntypedFormControl(),
        sublocality: new UntypedFormControl(), // administration level 3+
        city: new UntypedFormControl(), // // administration level 2
        state: new UntypedFormControl(), // // administration level 1
        country: new UntypedFormControl(),
        zip: new UntypedFormControl(),
      })
    }

    this.onSetCountyField(
      this.isCountyFieldVisible(this._value.country),
      this.form.get('county')?.value ?? this._value?.county,
    )

    this.countyFieldVisible$ = this.form.get('country').valueChanges.pipe(
      startWith(null),
      map(() => this.isCountyFieldVisible()),
      tap((visible: boolean) => this.onSetCountyField(visible)),
    )

    const countryCode = this._value?.country || this.defaultCountry
    // this.handleZipValidation(countryCode)
    this.states = States[countryCode]

    this.filteredCountries.next(this.countries.slice())
    this.countryFilterCtrl.valueChanges
      .pipe(takeUntil(this._onDestroy))
      .subscribe(() => {
        this.filterCountry()
      })

    this.filteredStates.next((this.states || []).slice())
    this.stateFilterCtrl.valueChanges
      .pipe(takeUntil(this._onDestroy))
      .subscribe(() => {
        this.filterState()
      })

    this.form.get('country').valueChanges.subscribe((country) => {
      this.onCountryChange(country)
    })

    this.form.valueChanges.subscribe((value) => {
      this.value = value
    })

    this.form.patchValue(this._value)
    this.form.updateValueAndValidity()

    // this.stateChanges.subscribe(() => {
    //   this.onCountryChange(this._value?.country || 'US')
    // })
  }

  ngAfterViewInit() {
    this.googleMapsApiLoaderService.google$
      .pipe(
        first(),
        tap((google) => {
          if (google) {
            if (this.placeSelector) {
              const interval = setInterval(() => {
                if (this.placeNameAutocompleteElement) {
                  this.startPlaceNameAutocomplete(google)
                  clearInterval(interval)
                }
              }, 1000)

              this._onDestroy.subscribe(() => clearInterval(interval))
            }

            if (this.addressAutocompleteElement) {
              this.startAddressAutocomplete(google)
            } else {
              const interval = setInterval(() => {
                if (this.addressAutocompleteElement) {
                  this.startAddressAutocomplete(google)
                  clearInterval(interval)
                }
              }, 1000)

              this._onDestroy.subscribe(() => clearInterval(interval))
            }
          }
        }),
      )
      .subscribe()
  }

  onCountryChange(countryCode: string) {
    // this.value.country = countryCode
    this.states = States[countryCode]
    this.filteredStates.next((this.states || []).slice())

    if (this.gaAutocomplete) {
      this.gaAutocomplete.setComponentRestrictions({
        country: countryCode.toLowerCase(),
      })
    }

    // this.handleZipValidation(countryCode)
  }

  onSetCountyField(isVisible: boolean, value?: string) {
    if (!isVisible) {
      this.form.removeControl('county')
      return
    }

    if (!this.form.get('county')) {
      this.form.addControl('county', new UntypedFormControl(value ?? ''))
    }
  }

  onStateChange() {
    this.form.get('county')?.setValue('')
  }

  // handleZipValidation(countryCode: string) {
  //   if (countryCode === 'US') {
  //     this.form.get('zip').setValidators([Validators.pattern(/^\d{5}(-\d{4})?$/)])
  //   } else {
  //     this.form.get('zip').setValidators([Validators.pattern(/^[a-z0-9][a-z0-9\- ]{0,10}[a-z0-9]$/i)])
  //   }
  //   this.form.get('zip').updateValueAndValidity()
  // }

  getErrorMessage(controlName?: string): string {
    return this.intakeService.getErrorMessage(controlName, this.form)
  }

  ngOnDestroy(): void {
    super.ngOnDestroy()
    this._onDestroy.next()
    this._onDestroy.complete()
  }

  protected beforeChange() {
    super.beforeChange()

    if (this._value) {
      if (this.form) {
        if (!isEqual(this.form.value, this._value)) {
          this.form.patchValue(this._value)
          this.form.updateValueAndValidity()
        }
      }
    } else if (isNil(this._value)) {
      this._value = {}
    }
  }

  private get placeNameAutocompleteElement() {
    const el = this.elRef.nativeElement
    let input: any

    if (this.placeSelector === true) {
      input = el.querySelector('input.place-name-selector')
    } else if (typeof this.placeSelector === 'string') {
      input = document.querySelector(this.placeSelector)
    } else if (this.placeSelector) {
      input = this.placeSelector
    } else {
      input = el.querySelector('input[name="address1"]')
    }

    return input
  }

  private get addressAutocompleteElement() {
    return this.elRef.nativeElement.querySelector('input[name="address1"]')
  }

  /**
   * Create or get the google autocomplete object
   */
  private startPlaceNameAutocomplete(google) {
    if (this.placeSelector != null && this.placeNameAutocompleteElement) {
      const autocomplete = new google.maps.places.Autocomplete(
        this.placeNameAutocompleteElement,
        {
          componentRestrictions: { country: this.defaultCountry.toLowerCase() },
          strictBounds: false,
          types: this.placeTypes ? this.placeTypes : ['establishment'],
        },
      )

      // When the user selects an address from the dropdown, populate the address fields in the form.
      autocomplete.addListener('place_changed', () =>
        this.fillInAddress(autocomplete),
      )
    }
  }

  private startAddressAutocomplete(google) {
    if (this.addressAutocompleteElement) {
      this.gaAutocomplete = new google.maps.places.Autocomplete(
        this.addressAutocompleteElement,
        {
          componentRestrictions: { country: this.defaultCountry.toLowerCase() },
          strictBounds: false,
          types: ['geocode'],
        },
      )

      // When the user selects an address from the dropdown, populate the address fields in the form.
      this.gaAutocomplete.addListener('place_changed', () =>
        this.fillInAddress(this.gaAutocomplete),
      )
    }
  }

  private fillInAddress(googleAutocomplete: any) {
    // All the fields has been reset as sometimes they are missing in result and may it retain old values.
    const value: AddressSchema = {
      sublocality: '',
      city: '',
      zip: '',
    }

    const componentForm = {
      locality: {
        which: 'long_name',
        id: 'city',
      },
      sublocality_level_1: {
        which: 'long_name',
        id: 'sublocality',
      },
      administrative_area_level_1: {
        which: 'short_name',
        id: 'state',
      },
      administrative_area_level_2: {
        which: 'long_name',
        id: 'county',
      },
      country: {
        which: 'short_name',
        id: 'country',
      },
      postal_code: {
        which: 'short_name',
        id: 'zip',
      },
    }

    // Get the place details from the autocomplete object.
    const place: GooglePlace = googleAutocomplete.getPlace()
    const street = []

    place.address_components.forEach((addressComponent) => {
      const addressType = addressComponent.types[0]
      if (addressType === 'street_number' || addressType === 'route') {
        street.push(addressComponent.short_name)
      }
    })

    value.address1 = street.join(' ')

    // Get each component of the address from the place details
    // and fill the corresponding field on the form.
    place.address_components.forEach((addressComponent) => {
      const addressType = addressComponent.types[0]

      // if we want to use this part of the address
      if (componentForm[addressType]) {
        const id = componentForm[addressType].id
        const val = addressComponent[componentForm[addressType].which]
        value[id] = val
        if (addressType === 'country') {
          // this.handleZipValidation(val)
        }
      }
    })

    if (
      value.state &&
      this.states?.length > 0 &&
      this.states.find((s) => s.value === value.state) == null
    ) {
      let long_name: string | undefined

      // Google Maps returns common abbreviations for some countries, so
      // we rely on the long name for comparison
      place.address_components.forEach((addressComponent) => {
        if (addressComponent.types[0] === 'administrative_area_level_1') {
          long_name = addressComponent.long_name
        }
      })

      const state = this.states.find(
        (s) => s.viewValue === long_name || s.abbreviation === value.state,
      )
      if (state != null) {
        value.state = state.value
      }
    }

    // sometimes Google Maps does not provide a city, because only a sublocality is used; this happens for places like Brooklyn, NY
    // which isn't a city, but the address will appear as "1st St, Brooklyn, NY" rather than "1st St, Brooklyn, New York City, NY"
    // in these situations, we just use the sublocality as the city
    if (!value.city && value.sublocality?.length > 0) {
      value.city = value.sublocality
      value.sublocality = ''
    }

    if (value.county && !this.countyFieldEnabled) {
      value.county = undefined
    }

    // this.value = value
    this.form.patchValue(value)
    this.form.updateValueAndValidity()
    // this.handleZipValidation(value.country || this.defaultCountry)

    this.placeSelected.emit(place)

    if (this.nameControl && place.name !== value.address1) {
      this.nameControl.setValue(place.name)
      this.nameControl.updateValueAndValidity()
    }

    if (
      this.phoneControl &&
      typeof place.international_phone_number === 'string' &&
      place.international_phone_number.length > 0
    ) {
      const phone = standardizePhone(place.international_phone_number)
      if (
        this.phoneControl.value != null &&
        this.phoneControl.value.length > 0 &&
        phone !== standardizePhone(this.phoneControl.value)
      ) {
        const confirmed = confirm(
          `Would you like to replace the phone number ${this.phoneControl.value} with ${place.name}'s listed number of ${phone}`,
        )
        if (confirmed) {
          this.phoneControl.setValue(phone)
          this.phoneControl.updateValueAndValidity()
        }
      } else if (phone) {
        this.phoneControl.setValue(phone)
        this.phoneControl.updateValueAndValidity()
      }
    }
  }

  private filterCountry() {
    if (!this.countries) {
      return
    }
    // get the search keyword
    let search = this.countryFilterCtrl.value
    if (!search) {
      this.filteredCountries.next(this.countries.slice())
      return
    } else {
      search = search.toLowerCase()
    }
    // filter the countries
    this.filteredCountries.next(
      this.countries.filter((country) =>
        country.viewValue.toLowerCase().includes(search),
      ),
    )
  }

  private filterState() {
    if (!this.states) {
      return
    }
    // get the search keyword
    let search = this.stateFilterCtrl.value
    if (!search) {
      this.filteredStates.next(this.states.slice())
      return
    } else {
      search = search.toLowerCase()
    }
    // filter the states
    this.filteredStates.next(
      this.states.filter((state) =>
        state.viewValue.toLowerCase().includes(search),
      ),
    )
  }

  private isCountyFieldVisible(country?: string): boolean {
    return this.countyFieldEnabled && (country ?? this.form.get('country').value) === 'US'
  }
}
